/**
 * The homepage hero's canvas: Wonda's pixel cat in tiny platform marks on a
 * dot lattice. `createHeroLattice` is self-contained (it uses only its
 * arguments and browser globals) because the hero also inlines its source in a
 * <script> right after the canvas, so the first frame paints while the page is
 * still parsing instead of after the app's JavaScript loads and hydrates.
 */

export type HeroLatticeData = {
  /** The cat, one string per lattice row: "#" where a mark goes. */
  rows: string[];
  /** Mark paths on a 24-unit box; each cat cell takes one, seeded. */
  marks: string[];
  /** Cells whose marks carry the accent (the pupils), with their radius. */
  pupils: [number, number][];
};

/** The pointer as the hero tracks it; `on` eases toward `target`. */
export type HeroPointer = { x: number; y: number; on: number; target: number };

export type HeroLattice = {
  /** Sizes the canvas to the section and places the cat. */
  layout: () => void;
  /** Paints one frame; returns whether marks are still easing. */
  draw: (pointer: HeroPointer) => boolean;
};

export function createHeroLattice(
  section: HTMLElement,
  canvas: HTMLCanvasElement,
  slot: HTMLElement,
  data: HeroLatticeData,
): HeroLattice | null {
  const INK = "rgba(22, 25, 28, 0.9)";
  const ACCENT = "#B308A7";
  const GRID_DOT = "rgba(22, 25, 28, 0.085)";
  // Below this size on a phone the marks stop reading as a cat; the lattice shows alone.
  const MIN_PHONE_CAT = 96;
  const PUPIL_RADIUS = 2.7;
  const CELLS = data.rows.length;

  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  const marks = data.marks.map((path) => new Path2D(path));
  const covered = new Uint8Array(CELLS * CELLS);
  data.rows.forEach((row, j) => {
    for (let i = 0; i < CELLS; i++)
      covered[j * CELLS + i] = row[i] === "#" ? 1 : 0;
  });

  type Mark = {
    x: number;
    y: number;
    kind: number;
    hot: boolean;
    ox: number;
    oy: number;
  };
  let width = 0;
  let dpr = 1;
  let pitch = 0;
  let grid: HTMLCanvasElement | null = null;
  let points: Mark[] = [];

  function layout() {
    const rect = section.getBoundingClientRect();
    width = rect.width;
    const height = rect.height;
    dpr = Math.min(2, window.devicePixelRatio || 1);
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);

    // Cat right of centre on desktop. On phones the slot (shown below md)
    // is the space between the nav and the copy, and the cat fills it.
    const phone = slot.offsetParent ? slot.getBoundingClientRect() : null;
    let size: number;
    let left: number;
    let top: number;
    if (phone) {
      size = Math.min(phone.width * 0.86, phone.height);
      left = phone.left - rect.left + (phone.width - size) / 2;
      top = phone.top - rect.top + (phone.height - size) / 2;
    } else {
      size = Math.min(window.innerHeight * 0.78, width * 0.46);
      const margin = Math.max(16, Math.min(46, width * 0.029));
      left = width - margin * 1.4 - size;
      top = (height - size) / 2 + height * 0.03;
    }
    const showCat = !phone || size >= MIN_PHONE_CAT;
    // Without the cat the lattice keeps the pitch of a full-width one.
    pitch = (showCat ? size : width * 0.86) / CELLS;
    const gx = Math.round(left / pitch);
    const gy = Math.round(top / pitch);
    const isCat = (i: number, j: number) =>
      showCat &&
      i >= 0 &&
      i < CELLS &&
      j >= 0 &&
      j < CELLS &&
      covered[j * CELLS + i] === 1;

    // Background dots on every lattice cell the cat doesn't take.
    grid = document.createElement("canvas");
    grid.width = canvas.width;
    grid.height = canvas.height;
    const gctx = grid.getContext("2d");
    if (!gctx) return;
    gctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    gctx.fillStyle = GRID_DOT;
    const cols = Math.ceil(width / pitch) + 1;
    const rowCount = Math.ceil(height / pitch) + 1;
    for (let j = 0; j < rowCount; j++) {
      for (let i = 0; i < cols; i++) {
        if (isCat(i - gx, j - gy)) continue;
        gctx.beginPath();
        gctx.arc(
          (i + 0.5) * pitch,
          (j + 0.5) * pitch,
          pitch * 0.17,
          0,
          Math.PI * 2,
        );
        gctx.fill();
      }
    }

    // One platform mark per cat cell; a fixed seed keeps the mix stable across resizes.
    let seed = 5;
    const rand = () => {
      seed = (seed * 16807) % 2147483647;
      return seed / 2147483647;
    };
    points = [];
    for (let j = 0; j < CELLS; j++) {
      for (let i = 0; i < CELLS; i++) {
        if (!isCat(i, j)) continue;
        points.push({
          x: (gx + i + 0.5) * pitch,
          y: (gy + j + 0.5) * pitch,
          kind: Math.floor(rand() * marks.length),
          hot: data.pupils.some(
            ([px, py]) => Math.hypot(i + 0.5 - px, j + 0.5 - py) < PUPIL_RADIUS,
          ),
          ox: 0,
          oy: 0,
        });
      }
    }
  }

  function draw(pointer: HeroPointer): boolean {
    if (!ctx || !grid) return false;
    pointer.on += (pointer.target - pointer.on) * 0.12;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(grid, 0, 0);
    const ink = new Path2D();
    const hot = new Path2D();
    const radius = Math.min(140, width * 0.12);
    const markScale = (pitch * 0.86) / 24;
    let moving = Math.abs(pointer.target - pointer.on) > 0.01;
    for (const point of points) {
      // The pointer pushes marks aside; they ease back when it leaves.
      const dx = point.x - pointer.x;
      const dy = point.y - pointer.y;
      const dist = Math.hypot(dx, dy);
      let tx = 0;
      let ty = 0;
      if (dist < radius && dist > 0.01) {
        const force = (1 - dist / radius) ** 2 * 30 * pointer.on;
        tx = (dx / dist) * force;
        ty = (dy / dist) * force;
      }
      point.ox += (tx - point.ox) * 0.18;
      point.oy += (ty - point.oy) * 0.18;
      if (Math.abs(tx - point.ox) > 0.05 || Math.abs(ty - point.oy) > 0.05) {
        moving = true;
      }
      const mark = marks[point.kind];
      if (!mark) continue;
      const x = point.x + point.ox;
      const y = point.y + point.oy;
      (point.hot ? hot : ink).addPath(
        mark,
        new DOMMatrix([
          markScale,
          0,
          0,
          markScale,
          x - 12 * markScale,
          y - 12 * markScale,
        ]),
      );
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = INK;
    ctx.fill(ink, "evenodd");
    ctx.fillStyle = ACCENT;
    ctx.fill(hot, "evenodd");
    return moving;
  }

  return { layout, draw };
}

/**
 * The inline <script> that paints the first frame during HTML parsing. It runs
 * inside the hero <section>, finds its canvas and phone slot, and repaints once
 * the web fonts settle (the phone slot's height depends on them) unless the
 * hero's effect has taken over by then (it sets `data-live` on the canvas).
 */
export function heroFirstPaintScript(data: HeroLatticeData): string {
  return `(function(){try{var s=document.currentScript&&document.currentScript.parentElement;var c=s&&s.querySelector("canvas");var slot=s&&s.querySelector("[data-hero-slot]");if(!c||!slot)return;var h=(${createHeroLattice.toString()})(s,c,slot,${JSON.stringify(data)});if(!h)return;var p={x:-9999,y:-9999,on:0,target:0};h.layout();h.draw(p);if(document.fonts&&document.fonts.ready){document.fonts.ready.then(function(){if(c.dataset.live===undefined){h.layout();h.draw(p);}});}}catch(e){}})();`;
}
